/**
 * <s-product-gallery> pairs a horizontally snapping main image track
 * ([data-gallery-track]) with thumbnail buttons. Thumbnails use
 * `commandfor` (this element's id), `command="--show-media"` and `data-media-index`.
 * The thumbnail for the visible image gets aria-current="true".
 */
class ProductGallery extends HTMLElement {
  #track = null;
  #frame = 0;

  #onCommand = (event) => {
    if (event.command !== '--show-media') return;
    const index = Number(event.source?.dataset?.mediaIndex);
    if (Number.isNaN(index)) return;
    this.#show(index);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#syncThumbs());
  };

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.addEventListener('command', this.#onCommand);
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#syncThumbs();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#onCommand);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #show(index) {
    const item = this.#track?.children[index];
    if (!item) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: item.offsetLeft - this.#track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#markCurrent(index);
  }

  #syncThumbs() {
    if (!this.#track || this.#track.clientWidth === 0) return;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / this.#track.clientWidth);
    this.#markCurrent(index);
  }

  #markCurrent(index) {
    for (const thumb of this.querySelectorAll('[command="--show-media"]')) {
      thumb.setAttribute('aria-current', Number(thumb.dataset.mediaIndex) === index ? 'true' : 'false');
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
